import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const inr = (n) => "\u20B9" + Number(n).toLocaleString("en-IN");

const ShopContext = createContext(null);

const read = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

const keyOf = (i) =>
  i.selections && i.selections.length ? `${i.id}::${i.selections.join("|")}` : i.id;

export function ShopProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [ratings, setRatings] = useState({});
  const [meta, setMeta] = useState({ families: [], shades: [], fabrics: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => read("lamsah_cart"));
  const [wishlist, setWishlist] = useState(() => read("lamsah_wishlist"));
  const [settings, setSettings] = useState({
    store_name: "LAMSAH", store_email: "Lamsahhijabs@gmail.com", instagram: "Lamsahksa",
    whatsapp: "", phone: "", currency: "INR",
    shipping_charge: 99, free_shipping_threshold: 1999, min_order_value: 0,
    delivery_info: "Dispatch within 2–3 business days across India. Complimentary shipping on qualifying orders. Easy returns within 14 days, unworn with tags attached.",
    announcement_enabled: false, announcement_text: "", payment_mode: "demo",
  });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/products`).then((r) => r.json()),
      fetch(`${API}/meta`).then((r) => r.json()),
      fetch(`${API}/settings`).then((r) => r.json()),
      fetch(`${API}/ratings`).then((r) => r.json()),
    ])
      .then(([p, m, s, rt]) => {
        setProducts(p.products || []);
        setMeta(m);
        if (s && s.store_name) setSettings(s);
        setRatings(rt || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    localStorage.setItem("lamsah_cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem("lamsah_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const addToCart = useCallback((id, qty = 1, silent = false, selections = null) => {
    const key = keyOf({ id, selections });
    setCart((c) => {
      const ex = c.find((i) => keyOf(i) === key);
      return ex
        ? c.map((i) => (keyOf(i) === key ? { ...i, qty: Math.min(i.qty + qty, 20) } : i))
        : [...c, { id, qty, ...(selections ? { selections } : {}) }];
    });
    if (!silent) toast.success("Added to your bag");
  }, []);

  const setQty = useCallback((key, qty) => {
    setCart((c) =>
      qty <= 0 ? c.filter((i) => keyOf(i) !== key) : c.map((i) => (keyOf(i) === key ? { ...i, qty } : i))
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((c) => c.filter((i) => keyOf(i) !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((id) => {
    setWishlist((w) => {
      const has = w.includes(id);
      toast.success(has ? "Removed from wishlist" : "Added to your wishlist");
      return has ? w.filter((x) => x !== id) : [...w, id];
    });
  }, []);

  const isWishlisted = useCallback((id) => wishlist.includes(id), [wishlist]);

  const cartItems = useMemo(
    () =>
      cart
        .map((i) => ({ ...i, key: keyOf(i), product: byId[i.id] }))
        .filter((i) => i.product),
    [cart, byId]
  );

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const subtotal = useMemo(
    () => cartItems.reduce((s, i) => s + i.product.price * i.qty, 0),
    [cartItems]
  );

  const value = {
    products, meta, loading, byId, settings, ratings,
    cart, cartItems, cartCount, subtotal,
    addToCart, setQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, isWishlisted,
  };
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  return useContext(ShopContext);
}

export function usePageTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} | LAMSAH — لمسة` : "LAMSAH | Luxury Hijabs — لمسة";
  }, [title]);
}
